import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errorHandler";

interface InstructorProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
  onComplete: () => void;
}

export function InstructorProfileDialog({ 
  open, 
  onOpenChange, 
  userId, 
  userEmail,
  onComplete 
}: InstructorProfileDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: userEmail,
    pin: "",
    joinDate: new Date().toISOString().split('T')[0],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    if (formData.fullName.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }
    
    if (formData.email.toLowerCase() !== userEmail.toLowerCase()) {
      toast.error("Email does not match your account email");
      return;
    }
    
    if (formData.pin.length !== 4 || !/^\d{4}$/.test(formData.pin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");

      // Call edge function to complete profile with elevated privileges
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/complete-instructor-profile`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fullName: formData.fullName.trim(),
            email: formData.email.toLowerCase().trim(),
            pin: formData.pin,
            joinDate: formData.joinDate,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to create profile");
      }

      toast.success("Profile created successfully!");
      onComplete();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Complete Instructor Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              You have been assigned as an instructor. Please complete your profile to continue.
            </p>
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                placeholder="Enter your full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Confirm your email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pin">Master PIN (4 digits) *</Label>
              <Input
                id="pin"
                type="password"
                required
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value.slice(0, 4) })}
                placeholder="Enter 4-digit PIN"
                maxLength={4}
                pattern="\d{4}"
              />
              <p className="text-xs text-muted-foreground">
                This PIN will be used for attendance and other secure actions
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="joinDate">Date of Joining *</Label>
              <Input
                id="joinDate"
                type="date"
                required
                value={formData.joinDate}
                onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating Profile..." : "Complete Profile"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}